/*
 * WW Tabs — generic ARIA-tab engine (D1 merge: three near-identical implementations — agentic / wearable / home-features — converge here)
 * Version: 1.6.0 - 2026-09-20 - grouped panel support for one tab controlling multiple content regions
 * Version-note: 1.5.0 - 2026-09-20 - orientation-aware arrow-key handling for vertical and horizontal tablists
 * Version-note: 1.4.0 - 2026-09-07 19:20 (UTC+8) - Plaud Tayler — opt-in [data-tabs-prefetch]: warms a panel's media on pointer/focus intent so display:none panels stay cheap on load without a blank first switch
 * Version-note: 1.4.0 - 2026-08-11 17:30 (UTC+8) - Plaud Tayler — direction stamp (data-tabs-dir=fwd|back on the root at each switch) + [data-tabs-slider] bridge: a slide-section inside the root mirrors tab state both ways (tab -> slideTo, slideChange -> activate), enabling true Swiper-driven panel motion
 * Version-note: 1.3.0 - 2026-08-11 16:20 (UTC+8) - Plaud Tayler — mouse-drag stepping on [data-tabs-swipe] (desktop parity for the "swipable card" switch styles; shared threshold/axis-lock with touch, native image drag suppressed in zones)
 * Version-note: 1.2.0 - 2026-08-10 12:15 (UTC+8) - Plaud Tayler — DTC-1025: touch-swipe stepping on [data-tabs-swipe] containers (horizontal swipe = prev/next tab; vertical scroll untouched)
 * Version-note: 1.1.0 - 2026-08-06 16:00 (UTC+8) - Plaud Tayler — [data-tabs-nav="prev|next"] arrow stepping (mobile modules replace the tab list with arrows; clamped, no wrap)
 * Version-note: 1.0.0 - 2026-08-05 18:20 - Plaud Tayler — first version
 * Usage: add data-ww-tabs="hidden|class" on the section root element (panel switch mode: hidden
 *   attribute, or .is-active class crossfade); optional data-tabs-home-end enables the Home/End keys.
 *   Triggers/panels keep the existing conventions [data-tab-trigger] / [data-tab-panel].
 * Extension point: every switch dispatches ww:tabchange on the root element (detail: {index,
 *   source: 'user'|'api'}) — consumers (e.g. wearable's video engine) listen to the event to attach
 *   business logic; programmatic switching goes through window.wwTabs.activate(root, i).
 * Document-level delegation, binds once, compatible with Section Rendering API hot-reload and multiple instances per page.
 */
(function () {
  'use strict';
  if (window.wwTabs) return;

  function tabsIn(root) { return Array.prototype.slice.call(root.querySelectorAll('[data-tab-trigger]')); }
  function panelsIn(root) { return Array.prototype.slice.call(root.querySelectorAll('[data-tab-panel]')); }

  function activate(root, i, source) {
    var tabs = tabsIn(root);
    if (i < 0 || i >= tabs.length) return;
    /* direction stamp: motion variants (e.g. wearable hslide) key enter-from-left/right off it
       - Plaud Tayler - 2026-08-11 17:30 */
    var prev = tabs.findIndex(function (t) { return t.classList.contains('is-active'); });
    root.setAttribute('data-tabs-dir', prev >= 0 && i < prev ? 'back' : 'fwd');
    tabs.forEach(function (t, j) {
      var on = j === i;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
    });
    var mode = root.getAttribute('data-ww-tabs') === 'class' ? 'class' : 'hidden';
    panelsIn(root).forEach(function (p, j) {
      var panelIndex = p.getAttribute('data-tab-panel-index');
      var panelPosition = panelIndex === null ? j : parseInt(panelIndex, 10);
      var panelIsActive = panelPosition === i;
      if (mode === 'class') p.classList.toggle('is-active', panelIsActive);
      else if (panelIsActive) p.removeAttribute('hidden');
      else p.setAttribute('hidden', '');
    });
    /* [data-tabs-current]: mirror the active tab's label (mobile arrow-nav modules) - Plaud Tayler - 2026-08-06 16:00 */
    var cur = root.querySelectorAll('[data-tabs-current]');
    Array.prototype.forEach.call(cur, function (c) { c.textContent = tabs[i].textContent.trim(); });
    /* bubbles: document-level consumers (e.g. the wearable video engine) rely on bubbling up to document */
    root.dispatchEvent(new CustomEvent('ww:tabchange', { bubbles: true, detail: { index: i, source: source || 'api' } }));
  }

  document.addEventListener('click', function (e) {
    var tab = e.target.closest && e.target.closest('[data-tab-trigger]');
    if (!tab) return;
    var root = tab.closest('[data-ww-tabs]');
    if (!root) return;
    activate(root, tabsIn(root).indexOf(tab), 'user');
  });

  document.addEventListener('keydown', function (e) {
    var k = e.key;
    var isArrow = k === 'ArrowUp' || k === 'ArrowDown' || k === 'ArrowLeft' || k === 'ArrowRight';
    var isJump = k === 'Home' || k === 'End';
    if (!isArrow && !isJump) return;
    var tab = e.target.closest && e.target.closest('[data-tab-trigger]');
    if (!tab) return;
    var root = tab.closest('[data-ww-tabs]');
    if (!root) return;
    var tablist = root.querySelector('[role="tablist"]');
    var orientation = tablist && tablist.getAttribute('aria-orientation') === 'vertical' ? 'vertical' : 'horizontal';
    var isPrev = orientation === 'vertical' ? k === 'ArrowUp' : k === 'ArrowLeft';
    var isNext = orientation === 'vertical' ? k === 'ArrowDown' : k === 'ArrowRight';
    if (isArrow && !isPrev && !isNext) return;
    if (isJump && !root.hasAttribute('data-tabs-home-end')) return;
    var tabs = tabsIn(root);
    var i = tabs.indexOf(tab);
    var n = i;
    if (isNext) n = (i + 1) % tabs.length;
    else if (isPrev) n = (i - 1 + tabs.length) % tabs.length;
    else if (k === 'Home') n = 0;
    else n = tabs.length - 1;
    e.preventDefault();
    activate(root, n, 'user');
    tabs[n].focus();
  });

  /* arrow stepping (mobile carousel-style modules) - Plaud Tayler - 2026-08-06 16:00 - Begin */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-tabs-nav]');
    if (!btn) return;
    var root = btn.closest('[data-ww-tabs]');
    if (!root) return;
    var tabs = tabsIn(root);
    var cur = tabs.findIndex(function (t) { return t.classList.contains('is-active'); });
    if (cur < 0) cur = 0;
    var next = btn.getAttribute('data-tabs-nav') === 'next' ? cur + 1 : cur - 1;
    if (next < 0 || next >= tabs.length) return; /* clamped, no wrap */
    activate(root, next, 'user');
  });
  /* arrow stepping - End - Plaud Tayler - 2026-08-06 16:00 */

  /* DTC-1025 touch-swipe stepping: mark a container [data-tabs-swipe] (inside a [data-ww-tabs]
     root) and a horizontal swipe steps prev/next like the arrows. Axis lock: gesture must be
     dominantly horizontal (>1.5x vertical) so page scrolling is never hijacked; passive
     listeners, no preventDefault - Plaud Tayler - 2026-08-10 - Begin */
  function stepFromSwipe(zone, dx, dy) {
    if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    var root = zone.closest('[data-ww-tabs]');
    if (!root) return;
    var tabs = tabsIn(root);
    var cur = tabs.findIndex(function (tb) { return tb.classList.contains('is-active'); });
    if (cur < 0) cur = 0;
    var next = dx < 0 ? cur + 1 : cur - 1; /* swipe left = next */
    if (next < 0 || next >= tabs.length) return; /* clamped, no wrap */
    activate(root, next, 'user');
  }

  var swipe = null;
  document.addEventListener('touchstart', function (e) {
    var zone = e.target.closest && e.target.closest('[data-tabs-swipe]');
    if (!zone) { swipe = null; return; }
    var t = e.touches[0];
    swipe = { zone: zone, x: t.clientX, y: t.clientY };
  }, { passive: true });

  document.addEventListener('touchend', function (e) {
    if (!swipe) return;
    var t = e.changedTouches[0];
    var s = swipe;
    swipe = null;
    stepFromSwipe(s.zone, t.clientX - s.x, t.clientY - s.y);
  }, { passive: true });
  /* touch-swipe stepping - End - Plaud Tayler - 2026-08-10 */

  /* mouse-drag stepping: desktop parity for the swipe zones ("swipable card" switch styles);
     same threshold + axis lock as touch. Drags starting on interactive elements are ignored,
     and native image dragging inside swipe zones is suppressed so the gesture can complete
     - Plaud Tayler - 2026-08-11 16:20 - Begin */
  var mdrag = null;
  document.addEventListener('mousedown', function (e) {
    if (e.button !== 0) { mdrag = null; return; }
    var zone = e.target.closest && e.target.closest('[data-tabs-swipe]');
    if (!zone || (e.target.closest && e.target.closest('a, button, input, select, textarea'))) { mdrag = null; return; }
    mdrag = { zone: zone, x: e.clientX, y: e.clientY };
  });

  document.addEventListener('mouseup', function (e) {
    if (!mdrag) return;
    var s = mdrag;
    mdrag = null;
    stepFromSwipe(s.zone, e.clientX - s.x, e.clientY - s.y);
  });

  document.addEventListener('dragstart', function (e) {
    if (e.target.closest && e.target.closest('[data-tabs-swipe]')) e.preventDefault();
  });
  /* mouse-drag stepping - End - Plaud Tayler - 2026-08-11 16:20 */

  /* slider-panel bridge: a slide-section marked [data-tabs-slider] inside a [data-ww-tabs] root
     mirrors the tab state in both directions — tab activate -> slideTo, swiper slideChange ->
     activate. Loop-safe: both sides no-op when already in sync - Plaud Tayler - 2026-08-11 17:30 - Begin */
  document.addEventListener('ww:tabchange', function (e) {
    var root = e.target;
    var host = root.querySelector && root.querySelector('slide-section[data-tabs-slider]');
    if (!host || !host.globalSlide || host.globalSlide.destroyed) return;
    if (host.globalSlide.activeIndex !== e.detail.index) host.globalSlide.slideTo(e.detail.index);
  });

  function bindSliderBridges() {
    document.querySelectorAll('slide-section[data-tabs-slider]').forEach(function (host) {
      if (host.__wwTabsSlider || host.__wwTabsSliderObs) return;
      var attach = function () {
        var sw = host.globalSlide;
        if (!sw || sw.destroyed) return false;
        host.__wwTabsSlider = true;
        sw.on('slideChange', function () {
          var root = host.closest('[data-ww-tabs]');
          if (!root) return;
          var tabs = tabsIn(root);
          var cur = tabs.findIndex(function (t) { return t.classList.contains('is-active'); });
          if (cur !== sw.activeIndex) activate(root, sw.activeIndex, 'slider');
        });
        return true;
      };
      if (attach()) return;
      /* swiper init is lazy (user-interaction gated); the init run stamps swiper-* classes */
      var mo = new MutationObserver(function () {
        if (attach()) { mo.disconnect(); host.__wwTabsSliderObs = null; }
      });
      mo.observe(host, { attributes: true, attributeFilter: ['class'] });
      host.__wwTabsSliderObs = mo;
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindSliderBridges);
  else bindSliderBridges();
  /* slider-panel bridge - End - Plaud Tayler - 2026-08-11 17:30 */

  /* media prefetch on tab intent - Plaud Tayler - 2026-09-07 19:20 - Begin
     A crossfade root keeps its inactive panels at display:none, which is what stops a three-tab
     module from fetching three large photos to show one. The cost is that the first switch can
     land on an empty frame. Warming the panel the moment a reader points at or focuses its tab
     buys back the switch without giving up the load-time saving: by the time the click lands the
     bytes are usually in cache, and a tab that is never approached is never fetched.
     Opt-in via [data-tabs-prefetch] on the root, so the shared engine's existing callers keep
     their current network behaviour. */
  function warm(panel) {
    if (!panel || panel.__wwWarmed) return;
    panel.__wwWarmed = true;
    Array.prototype.forEach.call(panel.querySelectorAll('img'), function (img) {
      var pic = img.parentElement;
      var pick = null;
      if (pic && pic.tagName === 'PICTURE') {
        var sources = pic.querySelectorAll('source');
        for (var i = 0; i < sources.length; i++) {
          var m = sources[i].getAttribute('media');
          if (!m || (window.matchMedia && window.matchMedia(m).matches)) { pick = sources[i]; break; }
        }
      }
      var srcset = (pick && pick.getAttribute('srcset')) || img.getAttribute('srcset');
      var sizes = (pick && pick.getAttribute('sizes')) || img.getAttribute('sizes');
      var pre = new Image();
      if (sizes) pre.sizes = sizes;
      if (srcset) pre.srcset = srcset;
      if (!srcset) pre.src = img.currentSrc || img.getAttribute('src') || '';
    });
  }

  function warmFromTrigger(t) {
    var root = t.closest('[data-ww-tabs][data-tabs-prefetch]');
    if (!root) return;
    var i = tabsIn(root).indexOf(t);
    if (i < 0) return;
    panelsIn(root).forEach(function (panel, position) {
      var panelIndex = panel.getAttribute('data-tab-panel-index');
      if ((panelIndex === null ? position : parseInt(panelIndex, 10)) === i) warm(panel);
    });
  }

  document.addEventListener('pointerover', function (e) {
    var t = e.target.closest && e.target.closest('[data-tab-trigger]');
    if (t) warmFromTrigger(t);
  });

  document.addEventListener('focusin', function (e) {
    var t = e.target.closest && e.target.closest('[data-tab-trigger]');
    if (t) warmFromTrigger(t);
  });
  /* media prefetch on tab intent - End - Plaud Tayler - 2026-09-07 19:20 */

  /* [data-tabs-autoplay="<seconds>"]: opt-in auto-advance. Absent attribute = no behaviour
     change for every existing module. Pauses on hover/focus inside the root, while the tab is
     backgrounded and while the root is off-screen; stops for good once the visitor drives the
     tabs themselves. Honours prefers-reduced-motion. - DTC-1442 */
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  function autoplayRoots() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-tabs-autoplay]'));
  }

  function setupAutoplay(root) {
    if (root.__wwTabsAutoplay) return;
    var seconds = parseFloat(root.getAttribute('data-tabs-autoplay'));
    if (!(seconds > 0)) return;
    if (reduceMotion && reduceMotion.matches) return;
    if (tabsIn(root).length < 2) return;

    var state = { timer: null, stopped: false, hovered: false, visible: true };
    root.__wwTabsAutoplay = state;

    function clear() { if (state.timer) { clearInterval(state.timer); state.timer = null; } }

    function tick() {
      var tabs = tabsIn(root);
      if (tabs.length < 2) return;
      var current = tabs.findIndex(function (t) { return t.classList.contains('is-active'); });
      activate(root, (current + 1) % tabs.length, 'autoplay');
    }

    function sync() {
      var shouldRun = !state.stopped && !state.hovered && state.visible && !document.hidden;
      if (shouldRun && !state.timer) state.timer = setInterval(tick, seconds * 1000);
      else if (!shouldRun) clear();
    }
    state.sync = sync;

    root.addEventListener('pointerenter', function () { state.hovered = true; sync(); });
    root.addEventListener('pointerleave', function () { state.hovered = false; sync(); });
    root.addEventListener('focusin', function () { state.hovered = true; sync(); });
    root.addEventListener('focusout', function (e) {
      if (!root.contains(e.relatedTarget)) { state.hovered = false; sync(); }
    });

    /* any switch the visitor drove (click / keyboard / swipe / arrows) retires autoplay */
    root.addEventListener('ww:tabchange', function (e) {
      if (e.detail && e.detail.source && e.detail.source !== 'autoplay') { state.stopped = true; clear(); }
    });

    if (window.IntersectionObserver) {
      state.visible = false;
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { state.visible = entry.isIntersecting; });
        sync();
      }, { threshold: 0.2 }).observe(root);
    }
    sync();
  }

  function initAutoplay() { autoplayRoots().forEach(setupAutoplay); }

  document.addEventListener('visibilitychange', function () {
    autoplayRoots().forEach(function (root) {
      if (root.__wwTabsAutoplay && root.__wwTabsAutoplay.sync) root.__wwTabsAutoplay.sync();
    });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAutoplay);
  else initAutoplay();
  document.addEventListener('shopify:section:load', initAutoplay);

  window.wwTabs = { activate: activate, tabsIn: tabsIn, initAutoplay: initAutoplay };
})();
